import {
  Component,
  Element,
  getAssetPath,
  h,
  Host,
  Prop,
  State,
  Watch
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";
import { config } from "../../common/config";
import {
  clearControlError,
  ControlValidation,
  validateControls
} from "../../common/form-validation";
import { Locale } from "../../common/locale";
import { FormSubmitResult } from "../../components";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/spacing",
  "utils/layout",
  "chameleon/scrollbar",
  "components/button",
  "components/edit",
  "components/tooltip"
];

@Component({
  tag: "gx-ide-name-picker",
  styleUrl: "name-picker.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/name-picker"]
})
export class GxIdeNamePicker {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #inputValueEl!: HTMLChEditElement;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/name-picker/shortcuts.json`);

  @Element() el: HTMLGxIdeNamePickerElement;

  /**
   * The input name
   */
  @State() inputValue: string;

  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * Callback that must be invoked when the 'Cancel' button is pressed.
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  /**
   * Callback that must be invoked when the 'Confirm' button is pressed.
   */
  @Prop() readonly confirmCallback: (
    inputValue: string
  ) => Promise<FormSubmitResult>;

  /**
   * This property is used to set the initial values of the input field when the component is loaded.
   */
  @Prop() readonly defaultInputValue: string;
  @Watch("defaultInputValue")
  defaultInputValueChanged(newDefaultInputValue: string) {
    this.inputValue = newDefaultInputValue;
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.defaultInputValueChanged(this.defaultInputValue);
  }

  componentDidLoad() {
    this.#initializeValidatableControls();
  }

  #handleInputValueChange = (event: CustomEvent<string> | InputEvent) => {
    this.inputValue = event.detail as string;
    clearControlError(this.validatableControls, "input-name");
  };

  #cancelClickHandler = async () => {
    this.cancelCallback();
  };

  #confirmClickHandler = async () => {
    const formSubmitResult = await this.confirmCallback(this.inputValue);
    this.validatableControls = validateControls(
      formSubmitResult,
      this.validatableControls
    );
  };

  #initializeValidatableControls = () => {
    // populate validatableControls with the controls that could have errors.
    const validatableControls: HTMLElement[] = [this.#inputValueEl];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  render() {
    const confirmButtonDisabled =
      !this.inputValue || this.inputValue.length === 0;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
        <section class="section">
          <div class="main scrollable field-group spacing-body">
            <div class="field field-block">
              <label class="label" htmlFor="input-name">
                {this.#componentLocale.labels.name}
              </label>
              <ch-edit
                type="text"
                id="input-name"
                class="input"
                placeholder={this.#componentLocale.placeHolders.name}
                value={this.inputValue}
                onInput={this.#handleInputValueChange}
                ref={(el: HTMLChEditElement) =>
                  (this.#inputValueEl = el as HTMLChEditElement)
                }
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#inputValueEl)}
            </div>
          </div>

          <footer class="dialog-footer-with-border spacing-body">
            <div class="buttons-spacer">
              <button
                id="cancel-button"
                class="button-secondary"
                onClick={this.#cancelClickHandler}
              >
                {this.#componentLocale.buttons.cancel}
              </button>

              <button
                id="confirm-button"
                class="button-primary"
                onClick={this.#confirmClickHandler}
                disabled={confirmButtonDisabled}
              >
                {this.#componentLocale.buttons.confirm}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}
